import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { useNotification } from '../../contexts/NotificationContext';
import InventoryService from '../../service/inventory.service';
import '../Inventory/inventory.css';

const UNIT_LABEL = { UNIT: 'un', KG: 'kg', G: 'g', L: 'L', ML: 'ml' };
const COMPATIBLE_UNITS = {
  UNIT: ['UNIT'],
  KG: ['KG', 'G'],
  G: ['KG', 'G'],
  L: ['L', 'ML'],
  ML: ['L', 'ML'],
};
const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const errorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  return (Array.isArray(message) ? message.join(', ') : message) || fallback;
};

function RecipeModal({ item, accountId, isOpen, toggle }) {
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isOpen || !item?.id || !accountId) return;
    let active = true;
    setLoading(true);
    setLoadError('');
    const loadInventory = async () => {
      const firstPage = await InventoryService.listItems(accountId, { page: 1, size: 100, active: true });
      if ((firstPage?.totalPages || 1) <= 1) return firstPage?.data || [];
      const remaining = await Promise.all(
        Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
          InventoryService.listItems(accountId, { page: index + 2, size: 100, active: true })
        )
      );
      return [...(firstPage.data || []), ...remaining.flatMap((page) => page?.data || [])];
    };
    Promise.all([InventoryService.getRecipe(accountId, item.id), loadInventory()])
      .then(([recipe, inventory]) => {
        if (!active) return;
        const mapTarget = (type) => (target) => ({
          type,
          targetId: target.id,
          name: target.name,
          components: (target.recipeComponents || []).map((component) => ({
            inventoryItemId: String(component.inventoryItemId),
            quantity: String(component.quantity),
            unit: component.unit,
          })),
        });
        setTargets([
          ...(recipe?.sizes || []).map(mapTarget('SIZE')),
          ...(recipe?.variants || []).map(mapTarget('VARIANT')),
        ]);
        setInventoryItems(inventory);
      })
      .catch((error) => {
        if (active) setLoadError(errorMessage(error, 'Não foi possível carregar a ficha técnica.'));
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [isOpen, item, accountId]);

  const inventoryById = useMemo(
    () => new Map(inventoryItems.map((inventoryItem) => [String(inventoryItem.id), inventoryItem])),
    [inventoryItems]
  );

  const updateComponent = (targetIndex, componentIndex, changes) => {
    setTargets((current) => current.map((target, index) => {
      if (index !== targetIndex) return target;
      return {
        ...target,
        components: target.components.map((component, innerIndex) =>
          innerIndex === componentIndex ? { ...component, ...changes } : component
        ),
      };
    }));
  };

  const selectIngredient = (targetIndex, componentIndex, inventoryItemId) => {
    const selected = inventoryById.get(inventoryItemId);
    updateComponent(targetIndex, componentIndex, {
      inventoryItemId,
      unit: selected?.unit || 'UNIT',
    });
  };

  const addComponent = (targetIndex) => {
    setTargets((current) => current.map((target, index) =>
      index === targetIndex
        ? { ...target, components: [...target.components, { inventoryItemId: '', quantity: '', unit: 'UNIT' }] }
        : target
    ));
  };

  const removeComponent = (targetIndex, componentIndex) => {
    setTargets((current) => current.map((target, index) =>
      index === targetIndex
        ? { ...target, components: target.components.filter((_, innerIndex) => innerIndex !== componentIndex) }
        : target
    ));
  };

  const save = async (event) => {
    event.preventDefault();
    for (const target of targets) {
      const ids = new Set();
      for (const component of target.components) {
        const inventoryItem = inventoryById.get(component.inventoryItemId);
        if (!inventoryItem || number(component.quantity) <= 0) {
          return notify('tr', 'warning', `Revise os ingredientes de "${target.name}"`, 'icon-alert-circle-exc');
        }
        if (ids.has(component.inventoryItemId)) {
          return notify('tr', 'warning', `O ingrediente "${inventoryItem.name}" está repetido em "${target.name}"`, 'icon-alert-circle-exc');
        }
        ids.add(component.inventoryItemId);
        if (!COMPATIBLE_UNITS[inventoryItem.unit]?.includes(component.unit)) {
          return notify('tr', 'warning', `A unidade de "${inventoryItem.name}" não é compatível com ${UNIT_LABEL[inventoryItem.unit]}`, 'icon-alert-circle-exc');
        }
      }
    }
    setSaving(true);
    try {
      await InventoryService.updateRecipe(accountId, item.id, {
        targets: targets.map(({ type, targetId, components }) => ({
          type,
          targetId,
          components: components.map((component) => ({
            inventoryItemId: Number(component.inventoryItemId),
            quantity: number(component.quantity),
            unit: component.unit,
          })),
        })),
      });
      notify('tr', 'success', 'Ficha técnica salva', 'icon-check-2');
      toggle();
    } catch (error) {
      notify('tr', 'danger', errorMessage(error, 'Não foi possível salvar a ficha técnica'), 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="recipe-modal" modalClassName="modal-form">
      <ModalHeader toggle={toggle}>Ficha técnica · {item?.name}</ModalHeader>
      <Form onSubmit={save}>
        <ModalBody>
          {loading ? (
            <div className="inventory-state"><Spinner color="primary" /><span>Carregando ficha técnica...</span></div>
          ) : loadError ? (
            <div className="inventory-state"><i className="tim-icons icon-alert-circle-exc" /><span>{loadError}</span></div>
          ) : targets.length === 0 ? (
            <div className="inventory-state"><strong>Sem tamanhos ou adicionais</strong><span className="text-muted">Cadastre uma medida no item antes de configurar a ficha técnica.</span></div>
          ) : (
            <>
              <p className="text-muted">Informe o consumo de cada ingrediente por unidade vendida. Tamanhos e adicionais sem ingredientes também serão preservados.</p>
              {targets.map((target, targetIndex) => (
                <section className="recipe-target" key={`${target.type}-${target.targetId}`}>
                  <div className="recipe-target-header">
                    <div><Badge color={target.type === 'SIZE' ? 'primary' : 'info'}>{target.type === 'SIZE' ? 'Tamanho' : 'Adicional'}</Badge><strong>{target.name}</strong></div>
                    <Button type="button" size="sm" color="primary" outline onClick={() => addComponent(targetIndex)}>
                      <i className="tim-icons icon-simple-add mr-1" /> Ingrediente
                    </Button>
                  </div>
                  {target.components.length === 0 ? (
                    <div className="recipe-empty">Nenhum ingrediente configurado.</div>
                  ) : target.components.map((component, componentIndex) => {
                    const selected = inventoryById.get(component.inventoryItemId);
                    const compatibleUnits = COMPATIBLE_UNITS[selected?.unit] || ['UNIT'];
                    const usedIds = new Set(target.components.filter((_, index) => index !== componentIndex).map((entry) => entry.inventoryItemId));
                    return (
                      <Row className="recipe-component" key={componentIndex}>
                        <Col md="6">
                          <FormGroup>
                            <Label>Ingrediente</Label>
                            <Input required type="select" value={component.inventoryItemId} onChange={(e) => selectIngredient(targetIndex, componentIndex, e.target.value)}>
                              <option value="">Selecione...</option>
                              {inventoryItems.map((inventoryItem) => (
                                <option key={inventoryItem.id} value={inventoryItem.id} disabled={usedIds.has(String(inventoryItem.id))}>
                                  {inventoryItem.name} ({UNIT_LABEL[inventoryItem.unit]})
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col xs="7" md="3">
                          <FormGroup>
                            <Label>Quantidade</Label>
                            <Input
                              required
                              type="number"
                              min="0"
                              step="any"
                              value={component.quantity}
                              onChange={(e) => updateComponent(targetIndex, componentIndex, { quantity: e.target.value })}
                            />
                          </FormGroup>
                        </Col>
                        <Col xs="5" md="2">
                          <FormGroup>
                            <Label>Unidade</Label>
                            <Input type="select" value={component.unit} onChange={(e) => updateComponent(targetIndex, componentIndex, { unit: e.target.value })}>
                              {compatibleUnits.map((unit) => <option key={unit} value={unit}>{UNIT_LABEL[unit]}</option>)}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col xs="12" md="1" className="recipe-remove">
                          <Button type="button" color="danger" className="btn-icon" size="sm" title="Remover ingrediente" onClick={() => removeComponent(targetIndex, componentIndex)}>
                            <i className="tim-icons icon-trash-simple" />
                          </Button>
                        </Col>
                      </Row>
                    );
                  })}
                </section>
              ))}
            </>
          )}
        </ModalBody>
        <ModalFooter className="recipe-modal-footer">
          <Button type="button" color="secondary" outline onClick={toggle}>Cancelar</Button>
          <Button type="submit" color="primary" disabled={loading || saving || Boolean(loadError)}>
            {saving ? 'Salvando...' : 'Salvar ficha técnica'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default RecipeModal;
